/* eslint-disable */
import type { Money } from "../../../common/currency.pb";
import type { BrowserInteraction } from "../../../franklin/browser_interaction.pb";
import type { Region } from "../../../franklin/region.pb";
import type { BlockerDescriptorTheme } from "../../../franklin/scenarios.pb";
import type { CardRequestInfo } from "../../../rewardly/common.pb";
import type { Birthdate } from "../../cashidv/common/Birthdate.pb";
import type { Image } from "../../ui/Image.pb";
import type { CallContext } from "../common/CallContext.pb";
import type { PasswordCreationDisplayConfig, PasswordVerificationDisplayConfig } from "../common/Passwords.pb";

export interface Flow {
  /**
   * Identifier of the flow, defined by clients.
   *
   * NOTE: THIS SHOULD NOT CONTAIN PII AS IT'S LOGGED AND DISPLAYED IN ADMIN TOOLING.
   */
  token?: string;
  customer_token?: string;
  /** Type of a flow. String value of the enums found at Type. */
  type?: string;
  parameters?: FlowParameters;
  intent?: FlowFlowIntent;
}

/** DO NOT RENAME ENUM VALUES EVEN WHEN TAG IS THE SAME. */
export enum FlowType {
  /** ORDER_PIZZA_FLOW - ORDER_PIZZA_FLOW and EXCEPTION_FLOW flows are for testing. */
  ORDER_PIZZA_FLOW = "ORDER_PIZZA_FLOW",
  EXCEPTION_FLOW = "EXCEPTION_FLOW",
  RECIPIENT_PAYMENT_FLOW = "RECIPIENT_PAYMENT_FLOW",
  /** REMOVE_BOOST - Removes selected Boost from the card. */
  REMOVE_BOOST = "REMOVE_BOOST",
  SENDER_PAYMENT_FLOW = "SENDER_PAYMENT_FLOW",
  /** ADD_BOOST - Adds or replaces an existing Boost on the card. */
  ADD_BOOST = "ADD_BOOST",
  /** ONBOARDING - Onboarding flow */
  ONBOARDING = "ONBOARDING",
  /** LOCK_ISSUED_CARD - Lock customer's primary issued card. */
  LOCK_ISSUED_CARD = "LOCK_ISSUED_CARD",
  /** DISABLE_ISSUED_CARD - Disable a customer's primary issued card. */
  DISABLE_ISSUED_CARD = "DISABLE_ISSUED_CARD",
  /** CHANGE_PASSCODE - Flow to change a customer's passcode. */
  CHANGE_PASSCODE = "CHANGE_PASSCODE",
  /** ACTIVATE_PHYSICAL_CARD - Activate physical card. */
  ACTIVATE_PHYSICAL_CARD = "ACTIVATE_PHYSICAL_CARD",
  /** @deprecated */
  REPORT_CARD_LOST_OR_STOLEN = "REPORT_CARD_LOST_OR_STOLEN",
  CHANGE_CARD_DESIGN = "CHANGE_CARD_DESIGN",
  REQUEST_PHYSICAL_CARD = "REQUEST_PHYSICAL_CARD",
  /**
   * IDENTITY_VERIFICATION - Any new product requirement should use the IDV_IDENTITY_VERIFICATION flow instead.
   *
   * @deprecated
   */
  IDENTITY_VERIFICATION = "IDENTITY_VERIFICATION",
  AMEND_CARD_MAILING_ADDRESS = "AMEND_CARD_MAILING_ADDRESS",
  LINK_PHONE_NUMBER = "LINK_PHONE_NUMBER",
  LINK_EMAIL_ADDRESS = "LINK_EMAIL_ADDRESS",
  HEAVY_CUSTOMER = "HEAVY_CUSTOMER",
  /** INITIATE_CRYPTO_PAYMENT - Flow to initiate the sending of a crypto (bitcoin) payment */
  INITIATE_CRYPTO_PAYMENT = "INITIATE_CRYPTO_PAYMENT",
  /** THIRD_PARTY_AUTHORIZATION - Authorization flow with a third party. */
  THIRD_PARTY_AUTHORIZATION = "THIRD_PARTY_AUTHORIZATION",
  MOBILE_CHECK_DEPOSIT = "MOBILE_CHECK_DEPOSIT",
  /** DISPUTE_CARD_TRANSACTION - No longer used */
  DISPUTE_CARD_TRANSACTION = "DISPUTE_CARD_TRANSACTION",
  SET_JURISDICTION = "SET_JURISDICTION",
  /** GET_DIRECT_DEPOSIT_FORM - Flow to generate and expose a PDF Direct Deposit Form. */
  GET_DIRECT_DEPOSIT_FORM = "GET_DIRECT_DEPOSIT_FORM",
  REPORT_LOST_CARD = "REPORT_LOST_CARD",
  ACTIVATE_PHYSICAL_CARD_IN_POSTCARD = "ACTIVATE_PHYSICAL_CARD_IN_POSTCARD",
  /** @deprecated */
  PEERMENTS_SENDER_FLOW = "PEERMENTS_SENDER_FLOW",
  /** ENABLE_ISSUED_CARD - Enable a customer's primary issued card. */
  ENABLE_ISSUED_CARD = "ENABLE_ISSUED_CARD",
  /** DISABLE_ISSUED_CARD_IN_POSTCARD - Disable a customer's primary issued card. */
  DISABLE_ISSUED_CARD_IN_POSTCARD = "DISABLE_ISSUED_CARD_IN_POSTCARD",
  /** GET_EVENT_BASED_BOOST_DETAIL - Flow to retrieve details on event based boost. */
  GET_EVENT_BASED_BOOST_DETAIL = "GET_EVENT_BASED_BOOST_DETAIL",
  /** UNLOCK_ISSUED_CARD - Unlock customer's primary issued card. */
  UNLOCK_ISSUED_CARD = "UNLOCK_ISSUED_CARD",
  /** ACCEPT_CRYPTO_PAYMENT - Flow to kick off receiving a crypto (bitcoin) payment */
  ACCEPT_CRYPTO_PAYMENT = "ACCEPT_CRYPTO_PAYMENT",
  ACTIVATE_DIRECT_DEPOSIT_ACCOUNT = "ACTIVATE_DIRECT_DEPOSIT_ACCOUNT",
  UNLOCK_QR_CODE_BOOST = "UNLOCK_QR_CODE_BOOST",
  /**
   * FRANKLIN_INVEST_EQUITY_CUSTOMER_ONBOARDING - Replaced by INVEST_EQUITY_CUSTOMER_ONBOARDING.
   *
   * @deprecated
   */
  FRANKLIN_INVEST_EQUITY_CUSTOMER_ONBOARDING = "FRANKLIN_INVEST_EQUITY_CUSTOMER_ONBOARDING",
  /** DDA_DETAILS - Flow to show DDA account details */
  DDA_DETAILS = "DDA_DETAILS",
  /** GOOGLE_PAY_PROVISIONING - Flow to provision customer's card with Google Pay in the app to app case */
  GOOGLE_PAY_PROVISIONING = "GOOGLE_PAY_PROVISIONING",
  TRANSFER_FUNDS = "TRANSFER_FUNDS",
  /** ACCEPT_INVEST_PAYMENT - Flow to kick off receiving an invest payment */
  ACCEPT_INVEST_PAYMENT = "ACCEPT_INVEST_PAYMENT",
  /** EDIT_PROFILE_BIO - Flow to edit your bio. */
  EDIT_PROFILE_BIO = "EDIT_PROFILE_BIO",
  /** REPORT_PROFILE - Flow to report a profile. */
  REPORT_PROFILE = "REPORT_PROFILE",
  /** DECLINE_CRYPTO_PAYMENT - Flow to kick off declining a crypto (bitcoin) payment */
  DECLINE_CRYPTO_PAYMENT = "DECLINE_CRYPTO_PAYMENT",
  /** SEND_INVEST_PAYMENT - Flow to initiate sending an invest payment */
  SEND_INVEST_PAYMENT = "SEND_INVEST_PAYMENT",
  /**
   * IDV_IDENTITY_VERIFICATION - Flow to verify customer's identity.
   * This is a replication of the IDENTITY_VERIFICATION flow which will be handled in cash-idv
   * instead of Franklin.
   */
  IDV_IDENTITY_VERIFICATION = "IDV_IDENTITY_VERIFICATION",
  /** INVEST_EQUITY_TRADE - Flow for equity trades (BUY, SELL) */
  INVEST_EQUITY_TRADE = "INVEST_EQUITY_TRADE",
  /** DEPOSIT_PAPER_CASH - Flow to deposit paper cash */
  DEPOSIT_PAPER_CASH = "DEPOSIT_PAPER_CASH",
  /** GET_BANKING_INTERSTITIAL - Flow to show features of Cash card from the Banking tab. */
  GET_BANKING_INTERSTITIAL = "GET_BANKING_INTERSTITIAL",
  /** TRANSFER_FROM_BANKS - Flow to show how customers can transfer money from other banks. */
  TRANSFER_FROM_BANKS = "TRANSFER_FROM_BANKS",
  /** CARD_EXPIRATION - Flow to notify customer their card is expiring and to reorder card. */
  CARD_EXPIRATION = "CARD_EXPIRATION",
  /** REQUEST_SPONSORSHIP - A flow for an under-18 account to request sponsorship from an adult. */
  REQUEST_SPONSORSHIP = "REQUEST_SPONSORSHIP",
  /** APPROVE_SPONSORSHIP - A flow for an adult to approve a sponsorship request from an under-18 account. */
  APPROVE_SPONSORSHIP = "APPROVE_SPONSORSHIP",
  /** INITIATE_TAX_UPGRADE - Flow to initiate tax upgrade/login */
  INITIATE_TAX_UPGRADE = "INITIATE_TAX_UPGRADE",
  /** DEPOSIT_PAPER_CASH_CONFIRMATION - Flow to confirm the deposit of paper cash */
  DEPOSIT_PAPER_CASH_CONFIRMATION = "DEPOSIT_PAPER_CASH_CONFIRMATION",
  /** ELECTRONIC_IDENTITY_VERIFICATION - Flow to perform electronic identity verification. */
  ELECTRONIC_IDENTITY_VERIFICATION = "ELECTRONIC_IDENTITY_VERIFICATION",
  /** GET_INSTANT_PAYOUT - Flow to allow customers to get their paycheck on demand */
  GET_INSTANT_PAYOUT = "GET_INSTANT_PAYOUT",
  /** UNLOCK_BANKING_DEPOSIT_OPTIONS - Flow to unlock banking deposit options. */
  UNLOCK_BANKING_DEPOSIT_OPTIONS = "UNLOCK_BANKING_DEPOSIT_OPTIONS",
  /** INITIATE_CRYPTO_WITHDRAWAL - Flow to initiate a crypto (bitcoin) withdrawal to the blockchain */
  INITIATE_CRYPTO_WITHDRAWAL = "INITIATE_CRYPTO_WITHDRAWAL",
  /** CLOSE_ACCOUNT - Flow to close a customer's account */
  CLOSE_ACCOUNT = "CLOSE_ACCOUNT",
  /** ENTER_QR_MARKETING_EVENT - Flow to enter a marketing event by scanning a QR code. */
  ENTER_QR_MARKETING_EVENT = "ENTER_QR_MARKETING_EVENT",
  /** SET_OR_UPDATE_PASSWORD - Flow to create a password or update it if it already exists. */
  SET_OR_UPDATE_PASSWORD = "SET_OR_UPDATE_PASSWORD",
  /** SET_OR_VERIFY_PASSWORD - Flow to verify a user's password or prompt to create one if it doesn't exist. */
  SET_OR_VERIFY_PASSWORD = "SET_OR_VERIFY_PASSWORD",
  /**
   * INITIATE_GLOBAL_PAYMENT - Flow to initiate a payment between customers of two different local networks
   * participating in the Network of Networks.
   */
  INITIATE_GLOBAL_PAYMENT = "INITIATE_GLOBAL_PAYMENT",
  /** INITIATE_CRYPTO_DEPOSIT_REVERSAL - Flow to initiate a crypto (bitcoin) deposit reversal (withdrawal) to an external wallet */
  INITIATE_CRYPTO_DEPOSIT_REVERSAL = "INITIATE_CRYPTO_DEPOSIT_REVERSAL",
  /** REQUIRE_NON_VOIP_PHONE_NUMBER - Flow to require that a user has a recently confirmed, non-voip phone number. */
  REQUIRE_NON_VOIP_PHONE_NUMBER = "REQUIRE_NON_VOIP_PHONE_NUMBER",
  /** INVEST_CRYPTO_CUSTOMER_ONBOARDING - Manages the customer onboarding flow for crypto investing */
  INVEST_CRYPTO_CUSTOMER_ONBOARDING = "INVEST_CRYPTO_CUSTOMER_ONBOARDING",
  /** ACCEPT_TERMS_OF_SERVICE_AGREEMENT - Flow to display and allow user to accept terms of service agreement */
  ACCEPT_TERMS_OF_SERVICE_AGREEMENT = "ACCEPT_TERMS_OF_SERVICE_AGREEMENT",
  /** RECOVER_PASSWORD - Flow to reset a user's password */
  RECOVER_PASSWORD = "RECOVER_PASSWORD",
  /** DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL - Flow to switch customer direct deposit via payroll login. */
  DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL = "DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL",
  /**
   * IDV_OR_SPONSORSHIP - A flow to verify customer's identity.
   * If the customer is between 13 and 18, request sponsorship from an adult.
   */
  IDV_OR_SPONSORSHIP = "IDV_OR_SPONSORSHIP",
  /** CARD_LSB_REORDER - A flow to notify customer their shazam card is about to terminate and to reorder card. */
  CARD_LSB_REORDER = "CARD_LSB_REORDER",
  /** RESEND_SPONSORSHIP_REQUEST - A flow to resend a sponsorship request. */
  RESEND_SPONSORSHIP_REQUEST = "RESEND_SPONSORSHIP_REQUEST",
  /** INITIATE_LIGHTNING_WITHDRAWAL - Flow to initiate a lightning withdrawal */
  INITIATE_LIGHTNING_WITHDRAWAL = "INITIATE_LIGHTNING_WITHDRAWAL",
  /** NEW_TO_BOOST_INFORMATION - A flow to provide Boost information for customers who have not interacted with Boosts. */
  NEW_TO_BOOST_INFORMATION = "NEW_TO_BOOST_INFORMATION",
  /** LINK_PAYROLL_ACCOUNT - A flow to provide payroll login primer and authenticate a customer with its payroll provider. */
  LINK_PAYROLL_ACCOUNT = "LINK_PAYROLL_ACCOUNT",
  /** SEND_PRINT_CHECK - HACKWEEK - A flow to allow customers to send checks from their account * */
  SEND_PRINT_CHECK = "SEND_PRINT_CHECK",
  /** COLLECT_ADDRESS - flow to collect addresses as part of IDV. */
  COLLECT_ADDRESS = "COLLECT_ADDRESS",
  /** TOTP - Flow for TOTP */
  TOTP = "TOTP",
  /** LOYALTY_ONBOARDING - flow to serve custom loyalty onboarding experience */
  LOYALTY_ONBOARDING = "LOYALTY_ONBOARDING",
  /** DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION - Flow to confirm switching customer's direct deposit via payroll. */
  DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION = "DIRECT_DEPOSIT_SWITCH_VIA_PAYROLL_CONFIRMATION",
  /** REQUIRE_SECOND_FACTOR_VERIFICATION - Flow to choose between non-voip phone verification vs TOTP based on customer settings */
  REQUIRE_SECOND_FACTOR_VERIFICATION = "REQUIRE_SECOND_FACTOR_VERIFICATION",
  /** CRYPTO_INVEST_CURRENCY_EXCHANGE - Flow to initiate exchange between FIAT currency and Stablecoin */
  CRYPTO_INVEST_CURRENCY_EXCHANGE = "CRYPTO_INVEST_CURRENCY_EXCHANGE",
  /** DD_SWITCH_UPSELL_CARD_ACTIVATED - Flow for upsell direct deposit switch at the end of a successful card activation. */
  DD_SWITCH_UPSELL_CARD_ACTIVATED = "DD_SWITCH_UPSELL_CARD_ACTIVATED",
  /** REVIEW_LOGIN - Flow to allow customers to verify or revoke a new device login */
  REVIEW_LOGIN = "REVIEW_LOGIN",
  /** CRYPTO_INVEST_ONBOARDING - Flow to onboard customers for crypto exchange */
  CRYPTO_INVEST_ONBOARDING = "CRYPTO_INVEST_ONBOARDING",
  /** P2P_REPORT_SCAM - Flow to report scam through P2P platform (scamly). */
  P2P_REPORT_SCAM = "P2P_REPORT_SCAM",
  /** REQUIREMENT_TEST_FLOW - Requirement Test Flow (productcannon) */
  REQUIREMENT_TEST_FLOW = "REQUIREMENT_TEST_FLOW",
  /** INITIATE_CASH_CARD_DISPUTE_CLAIM - Flow to initiate a Cash Card dispute */
  INITIATE_CASH_CARD_DISPUTE_CLAIM = "INITIATE_CASH_CARD_DISPUTE_CLAIM",
  /** GRANTLY_MANAGED_GRANT - Flow to create a Grant via Grantly. */
  GRANTLY_MANAGED_GRANT = "GRANTLY_MANAGED_GRANT",
  /** CRYPTO_INVEST_CONFIGURE_PAYROLL - Flow to configure payroll to crypto currency automatic conversion AKA Paid In Bitcoin. */
  CRYPTO_INVEST_CONFIGURE_PAYROLL = "CRYPTO_INVEST_CONFIGURE_PAYROLL",
  /** ACCEPT_GIFT_CARD_PAYMENT - Flow to accept a gift card */
  ACCEPT_GIFT_CARD_PAYMENT = "ACCEPT_GIFT_CARD_PAYMENT",
  /** CANCEL_INVEST_PAYMENT - Flow to cancel an invest payment */
  CANCEL_INVEST_PAYMENT = "CANCEL_INVEST_PAYMENT",
  /** ROUND_UP_ONBOARDING - Flow to onboard to Round Up */
  ROUND_UP_ONBOARDING = "ROUND_UP_ONBOARDING",
  /** CHANGE_ROUND_UP_DESTINATION - Flow to change RoundUp destination */
  CHANGE_ROUND_UP_DESTINATION = "CHANGE_ROUND_UP_DESTINATION",
  /** INITIATE_BANK_ACCOUNT_LINKING - Flow to initiate bank linking */
  INITIATE_BANK_ACCOUNT_LINKING = "INITIATE_BANK_ACCOUNT_LINKING",
  /** POST_SIGN_IN - Flow to gather required post sign-up information during onboarding */
  POST_SIGN_IN = "POST_SIGN_IN",
  /** SERVE_AND_VERIFY_CHALLENGE - Flow to verify a topic (i.e. sms, email) by sending a challenge code */
  SERVE_AND_VERIFY_CHALLENGE = "SERVE_AND_VERIFY_CHALLENGE",
  /** SET_OR_UPDATE_TRUSTED_CONTACT - Flow to set or update a customer's Trusted Contact details. */
  SET_OR_UPDATE_TRUSTED_CONTACT = "SET_OR_UPDATE_TRUSTED_CONTACT",
  /** INITIATE_FIATLY_PAYMENT - Flow to initiate p2p-fiat payment using Fiatly. */
  INITIATE_FIATLY_PAYMENT = "INITIATE_FIATLY_PAYMENT",
  /** ACQUIRE_ALIAS - Flow to add an SMS or email alias to an account */
  ACQUIRE_ALIAS = "ACQUIRE_ALIAS",
  /** INITIATE_DISPUTE_CLAIM - Flow to initiate a dispute about something in the customer's activity. */
  INITIATE_DISPUTE_CLAIM = "INITIATE_DISPUTE_CLAIM",
  /** FAMILY_ACCOUNT_STATE - Family account state flow to setup family accounts when customer has no sponsorships or show current family members. */
  FAMILY_ACCOUNT_STATE = "FAMILY_ACCOUNT_STATE",
  /**
   * PROVISION_GOOGLE_PAY_IN_APP - Flow to provision customer's card with Google Pay in the app itself
   * (as opposed to the app to app case via GOOGLE_PAY_PROVISIONING)
   */
  PROVISION_GOOGLE_PAY_IN_APP = "PROVISION_GOOGLE_PAY_IN_APP",
  /** PAYMENT_FLOW - Legacy Flow to initiate p2p payment. Now supported by Fiatly. */
  PAYMENT_FLOW = "PAYMENT_FLOW",
  /** PROVISION_APPLE_PAY - Flow to provision customer's Cash card with Apple Pay (both in-app and app-to-app) */
  PROVISION_APPLE_PAY = "PROVISION_APPLE_PAY",
  /** INITIATE_P2P_PAYMENT_DISPUTE_CLAIM - Flow to initiate a dispute on a p2p payment. */
  INITIATE_P2P_PAYMENT_DISPUTE_CLAIM = "INITIATE_P2P_PAYMENT_DISPUTE_CLAIM",
  /** REMOVE_TRUSTED_CONTACT - Flow to remove a customers Trusted Contact. */
  REMOVE_TRUSTED_CONTACT = "REMOVE_TRUSTED_CONTACT",
  /** INITIATE_AFTERPAY_PAYMENT - Flow to initiate an Afterpay payment */
  INITIATE_AFTERPAY_PAYMENT = "INITIATE_AFTERPAY_PAYMENT",
  /** LINK_NEW_PAYMENT_CARD - Flow to link a new card * */
  LINK_NEW_PAYMENT_CARD = "LINK_NEW_PAYMENT_CARD",
  /** CONVERT_GIFT_CARD_TO_CASH - Flow to convert gift card to cash. */
  CONVERT_GIFT_CARD_TO_CASH = "CONVERT_GIFT_CARD_TO_CASH",
  /** NEON_EXAMPLE_GIFT_WRAP - Flow for a Neon exemplar project. */
  NEON_EXAMPLE_GIFT_WRAP = "NEON_EXAMPLE_GIFT_WRAP",
  /** ENABLE_AND_UNMASK_ISSUED_CARD - Enable customer's primary issued card and unmask card numbers. */
  ENABLE_AND_UNMASK_ISSUED_CARD = "ENABLE_AND_UNMASK_ISSUED_CARD",
  /** DIRECT_DEPOSIT_SWITCH_UPSELL - Flow for upsell direct deposit switch. To be used in different contexts: card ordering, activation, etc. */
  DIRECT_DEPOSIT_SWITCH_UPSELL = "DIRECT_DEPOSIT_SWITCH_UPSELL",
  /** GRADUATE_SPONSORSHIP - Graduate from a sponsorship to direct account ownership. */
  GRADUATE_SPONSORSHIP = "GRADUATE_SPONSORSHIP",
  /** VERIFY_PASSCODE_OR_ENABLE_SECURITY_LOCK - Verify a passcode or require the user to enable a security lock if they have not done so */
  VERIFY_PASSCODE_OR_ENABLE_SECURITY_LOCK = "VERIFY_PASSCODE_OR_ENABLE_SECURITY_LOCK",
  /** VERIFY_PASSCODE_IF_SECURITY_LOCK_ENABLED - Verify a passcode if the user has security lock enabled */
  VERIFY_PASSCODE_IF_SECURITY_LOCK_ENABLED = "VERIFY_PASSCODE_IF_SECURITY_LOCK_ENABLED",
  /** CHANGE_AFTERPAY_PAYMENT_DATE - Change upcoming payment date for an Afterpay order */
  CHANGE_AFTERPAY_PAYMENT_DATE = "CHANGE_AFTERPAY_PAYMENT_DATE",
  /** CANCEL_PENDING_ROUND_UP_EXECUTION - Flow to cancel a pending Round Up execution */
  CANCEL_PENDING_ROUND_UP_EXECUTION = "CANCEL_PENDING_ROUND_UP_EXECUTION",
  /** ADD_EMAIL_ALIAS - Flow to add an email alias to a customer's account */
  ADD_EMAIL_ALIAS = "ADD_EMAIL_ALIAS",
  /** ADD_SMS_ALIAS - Flow to add an SMS alias to a customer's account */
  ADD_SMS_ALIAS = "ADD_SMS_ALIAS",
  /** ENABLE_SECURITY_LOCk - Flow to enable a user's security lock */
  ENABLE_SECURITY_LOCk = "ENABLE_SECURITY_LOCk",
  /** DISABLE_SECURITY_LOCK - Flow to disable a user's security lock */
  DISABLE_SECURITY_LOCK = "DISABLE_SECURITY_LOCK",
  /** SET_PASSCODE - Flow to set a passcode or do nothing if it is already set */
  SET_PASSCODE = "SET_PASSCODE",
  /** SET_OR_UPDATE_PASSCODE - Flow to set a passcode, or change it if already set */
  SET_OR_UPDATE_PASSCODE = "SET_OR_UPDATE_PASSCODE",
  /** RESET_PASSCODE - Flow to allow a user to reset their passcode */
  RESET_PASSCODE = "RESET_PASSCODE",
  /**
   * ELIGIBILITY_RESOLUTION - Reserved name for eligibility checks. https://github.com/squareup/cash-eligibility
   * https://www.notion.so/cashappcash/Eligibility-framework-be2496749ea342d3b153e13ccdcfff3a
   */
  ELIGIBILITY_RESOLUTION = "ELIGIBILITY_RESOLUTION",
  INIT_REMIT = "INIT_REMIT",
  /** SEND_GIFT_CARD_PAYMENT - Customer send gift card to one or more customers */
  SEND_GIFT_CARD_PAYMENT = "SEND_GIFT_CARD_PAYMENT",
  /** EXEMPLAR_ORDER_PIZZA - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA = "EXEMPLAR_ORDER_PIZZA",
  /** EXEMPLAR_ORDER_PIZZA_WITH_SURVEY - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_WITH_SURVEY = "EXEMPLAR_ORDER_PIZZA_WITH_SURVEY",
  /** EDIT_CUSTOMER_BIO - A new flow to replace EDIT_PROFILE_BIO in cash-customer-profile */
  EDIT_CUSTOMER_BIO = "EDIT_CUSTOMER_BIO",
  /** INVEST_TEEN_REQUEST_AUTHORIZATION - Allows teen user to request investing authorization */
  INVEST_TEEN_REQUEST_AUTHORIZATION = "INVEST_TEEN_REQUEST_AUTHORIZATION",
  /** INVEST_SPONSOR_AUTHORIZATION_APPROVAL - Allows teen sponsor to approve authorization */
  INVEST_SPONSOR_AUTHORIZATION_APPROVAL = "INVEST_SPONSOR_AUTHORIZATION_APPROVAL",
  /** CONFIRM_VERIFIED_IDENTITY - Prompts a customer to confirm their already verified identity */
  CONFIRM_VERIFIED_IDENTITY = "CONFIRM_VERIFIED_IDENTITY",
  /** INITIATE_BITCOIN_WITHDRAWAL - Flow to initiate a bitcoin withdrawal. This may be a lightning or on-chain withdrawal. */
  INITIATE_BITCOIN_WITHDRAWAL = "INITIATE_BITCOIN_WITHDRAWAL",
  /** INITIATE_SPLIT - Initiate a split bill request * */
  INITIATE_SPLIT = "INITIATE_SPLIT",
  /** PAY_BILL - Pay a bill * */
  PAY_BILL = "PAY_BILL",
  CUSTOMER_STRIKE_WARNING = "CUSTOMER_STRIKE_WARNING",
  /** CRYPTO_ONBOARDING - Flow to become eligible to Crypto features * */
  CRYPTO_ONBOARDING = "CRYPTO_ONBOARDING",
  /** SUP_BNPL_CHECKOUT - Flow for single use payment checkout with Afterpay * */
  SUP_BNPL_CHECKOUT = "SUP_BNPL_CHECKOUT",
  /** CREATE_OR_TOGGLE_PASSCODE - Flow for creating a Cash Pin or Toggling on/off Cash Pin * */
  CREATE_OR_TOGGLE_PASSCODE = "CREATE_OR_TOGGLE_PASSCODE",
  /**
   * INVEST_EQUITY_CUSTOMER_ONBOARDING_TRAMPOLINE - DEPRECATED: Flow for enacting the Invest Equity Customer Onboarding as a Franklin subflow.
   *
   * @deprecated
   */
  INVEST_EQUITY_CUSTOMER_ONBOARDING_TRAMPOLINE = "INVEST_EQUITY_CUSTOMER_ONBOARDING_TRAMPOLINE",
  /** INITIATE_OUTBOUND_PHONE_SUPPORT - Flow for initiating a phone support callback request * */
  INITIATE_OUTBOUND_PHONE_SUPPORT = "INITIATE_OUTBOUND_PHONE_SUPPORT",
  /**
   * SOURCE_OF_FUNDS_EMPLOYMENT_TRAMPOLINE - DEPRECATED: Flow for enacting the Source of Funds Employment as a Franklin subflow.
   *
   * @deprecated
   */
  SOURCE_OF_FUNDS_EMPLOYMENT_TRAMPOLINE = "SOURCE_OF_FUNDS_EMPLOYMENT_TRAMPOLINE",
  /**
   * INVEST_WHERE_YOU_SHOP_ONBOARDING - Flow for onboarding onto the Invest-Where-You-Shop feature.
   * This is for the 2022Q3 Financial Services Hackweek
   */
  INVEST_WHERE_YOU_SHOP_ONBOARDING = "INVEST_WHERE_YOU_SHOP_ONBOARDING",
  /** EXEMPLAR_ORDER_PIZZA_VIA_SUBFLOW - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_VIA_SUBFLOW = "EXEMPLAR_ORDER_PIZZA_VIA_SUBFLOW",
  /** EXEMPLAR_ORDER_PIZZA_WITH_IDV - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_WITH_IDV = "EXEMPLAR_ORDER_PIZZA_WITH_IDV",
  /** GET_NON_RECOURSE_PAYOUT - Flow is an alternative to the GET_INSTANT_PAYOUT flow for users who cannot provide payroll data. */
  GET_NON_RECOURSE_PAYOUT = "GET_NON_RECOURSE_PAYOUT",
  /** SET_SAVINGS_GOAL - Set a savings goal as part of savings product. */
  SET_SAVINGS_GOAL = "SET_SAVINGS_GOAL",
  /** SAVINGS_NUX - Flow for onboarding customers to savings product. */
  SAVINGS_NUX = "SAVINGS_NUX",
  /** WIRE_TRANSFER - Flow to display wire transfer details to a customer. */
  WIRE_TRANSFER = "WIRE_TRANSFER",
  /** ORDER_TIGERS - Flow to order tigers via a fictional product, Tigers on Demand. For demo purposes only. */
  ORDER_TIGERS = "ORDER_TIGERS",
  /** SUP_BNPL_CANCEL_CARD - Flow to cancel a generated SUP card for SUP_BNPL_CHECKOUT FLOW * */
  SUP_BNPL_CANCEL_CARD = "SUP_BNPL_CANCEL_CARD",
  /** SUP_BNPL_UPDATE_CARD_AMOUNT - Flow to adjust amount on a generated SUP card for SUP_BNPL_CHECKOUT flow * */
  SUP_BNPL_UPDATE_CARD_AMOUNT = "SUP_BNPL_UPDATE_CARD_AMOUNT",
  /** IN_TRANSACTION_TOP_UP - Flow to initiate in-transaction top-up experience * */
  IN_TRANSACTION_TOP_UP = "IN_TRANSACTION_TOP_UP",
  /** INVITE_FRIENDS - Flow to invite friends * */
  INVITE_FRIENDS = "INVITE_FRIENDS",
  /** BANK_AND_WIRE_TRANSFERS - This flow will be kicked off if a user has not gone through idv but should get access to Wires. */
  BANK_AND_WIRE_TRANSFERS = "BANK_AND_WIRE_TRANSFERS",
  /** ACCEPT_UPDATED_CARD_TERMS - Flow to display and allow user to accept updated Cash Card terms of service agreement */
  ACCEPT_UPDATED_CARD_TERMS = "ACCEPT_UPDATED_CARD_TERMS",
  /** REFRESH_LIGHTNING_INVOICE - Flow to report on the payment status for a lightning invoice */
  REFRESH_LIGHTNING_INVOICE = "REFRESH_LIGHTNING_INVOICE",
  /**
   * LIMITS_SOURCE_OF_FUNDS_COLLECTION - Flow to collect a customer's source of wealth/funds information for increasing or
   * deceasing their limits
   */
  LIMITS_SOURCE_OF_FUNDS_COLLECTION = "LIMITS_SOURCE_OF_FUNDS_COLLECTION",
  /** SEND_P2P_FIAT_PAYMENT - Sends a p2p fiat payment. */
  SEND_P2P_FIAT_PAYMENT = "SEND_P2P_FIAT_PAYMENT",
  /** ACCEPT_P2P_FIAT_PAYMENT - Accepts a p2p fiat payment as the recipient. */
  ACCEPT_P2P_FIAT_PAYMENT = "ACCEPT_P2P_FIAT_PAYMENT",
  /** CREATE_GROUP - Create a group * */
  CREATE_GROUP = "CREATE_GROUP",
  /** LEAVE_GROUP - Leave a group * */
  LEAVE_GROUP = "LEAVE_GROUP",
  /** CREATE_GROUP_EXPENSE - Create an expense within a group. * */
  CREATE_GROUP_EXPENSE = "CREATE_GROUP_EXPENSE",
  /** FIND_MONEY_RETAILERS - Shows retailers where money can be withdrawn */
  FIND_MONEY_RETAILERS = "FIND_MONEY_RETAILERS",
  /** CRYPTO_SOURCE_OF_FUNDS_COLLECTION - Flow to collect a customer's source of wealth/funds information for crypto onboarding */
  CRYPTO_SOURCE_OF_FUNDS_COLLECTION = "CRYPTO_SOURCE_OF_FUNDS_COLLECTION",
  /** AUTHENTICATE_TRANSACTIONS - Flow to handle potentially unrecognized auth'd transactions and side effects. */
  AUTHENTICATE_TRANSACTIONS = "AUTHENTICATE_TRANSACTIONS",
  /** ENROLL_IN_LOYALTY - Lets customers enroll in a loyalty program */
  ENROLL_IN_LOYALTY = "ENROLL_IN_LOYALTY",
  /** DOCUMENT_IDENTITY_VERIFICATION - Verify a customer's identity by providing a government id and selfie */
  DOCUMENT_IDENTITY_VERIFICATION = "DOCUMENT_IDENTITY_VERIFICATION",
  /** GROUP_EXPENSE_SLICE_P2P_ACTION - Take a p2p action on an expense slice in a group context * */
  GROUP_EXPENSE_SLICE_P2P_ACTION = "GROUP_EXPENSE_SLICE_P2P_ACTION",
  /** SUP_BNPL_MANAGE_CARD - Flow for managing single-use payment cards (update amount subflow, cancel subflow) * */
  SUP_BNPL_MANAGE_CARD = "SUP_BNPL_MANAGE_CARD",
  /** SANCTIONS_INTERDICTION_INFO_COLLECTION - Flow to collect customer's information when their payment held for sanctions review * */
  SANCTIONS_INTERDICTION_INFO_COLLECTION = "SANCTIONS_INTERDICTION_INFO_COLLECTION",
  /**
   * REVIEW_BUSINESS_ACCOUNT - Deprecated in favor of REVIEW_BUSINESS_ACCOUNT_TAX. This flow was never registered. *
   *
   * @deprecated
   */
  REVIEW_BUSINESS_ACCOUNT = "REVIEW_BUSINESS_ACCOUNT",
  /** UPDATE_BUSINESS_ACCOUNT_TAX_INFO - Flow to collect cash business account customers' tax information. */
  UPDATE_BUSINESS_ACCOUNT_TAX_INFO = "UPDATE_BUSINESS_ACCOUNT_TAX_INFO",
  /** REVIEW_BUSINESS_ACCOUNT_TAX - Flow with information on new tax rules for cash business account customers * */
  REVIEW_BUSINESS_ACCOUNT_TAX = "REVIEW_BUSINESS_ACCOUNT_TAX",
  /** PIGGYBANK_SAVINGS_ROUND_UP - Flow to manage customer savings round up status from savings home * */
  PIGGYBANK_SAVINGS_ROUND_UP = "PIGGYBANK_SAVINGS_ROUND_UP",
  /** ORDER_LIONS - Flow to order lions via a fictional product, Lions on Demand. For demo purposes only. */
  ORDER_LIONS = "ORDER_LIONS",
  /**
   * ELIGIBLE_ORDER_LIONS - Flow to validate order lions eligibility via a fictional product, Lions on Demand.
   * For demo purposes only.
   */
  ELIGIBLE_ORDER_LIONS = "ELIGIBLE_ORDER_LIONS",
  /** DOWNGRADE_BUSINESS_ACCOUNT - Flow for business account downgrade * */
  DOWNGRADE_BUSINESS_ACCOUNT = "DOWNGRADE_BUSINESS_ACCOUNT",
  /** EXEMPLAR_ORDER_PIZZA_MULTI - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_MULTI = "EXEMPLAR_ORDER_PIZZA_MULTI",
  /**
   * INSTANT_PAY_DIRECT_DEPOSIT_SWITCH_OFFER - Instant Paycheck will offer the customer money in exchange for a direct deposit switch to
   * CashApp.  Instant Paycheck will also help them switch via their payroll provider (or manually).
   */
  INSTANT_PAY_DIRECT_DEPOSIT_SWITCH_OFFER = "INSTANT_PAY_DIRECT_DEPOSIT_SWITCH_OFFER",
  /** ORDER_CATS - Flow to order lions via a fictional product, Lions on Demand. For demo purposes only. */
  ORDER_CATS = "ORDER_CATS",
  /**
   * BORROW_UNLOCKED - Borrow will inform the customer that their credit line has been unlocked and allow then
   * to take their first loan.
   */
  BORROW_UNLOCKED = "BORROW_UNLOCKED",
  /**
   * LINK_ACCOUNT_PRE_AUTH - DEPRECATED: This user flow still exists but is now handled as part of the
   * `GRANTLY_MANAGED_GRANT` flow (pre auth requirement) - [GrantFlow](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/GrantFlow.kt).
   * See [LinkAccountPreAuthorizationHandler](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/actionhandlers/linkaccount/LinkAccountPreAuthorizationHandler.kt).
   *
   * Prompt a user to link their Cash account to a third-party account prior to creating
   * a Link Account Grant. This flow should only be triggered as a subflow from within Grantly's
   * Pre-Authorization Requirement Handler.
   *
   * @deprecated
   */
  LINK_ACCOUNT_PRE_AUTH = "LINK_ACCOUNT_PRE_AUTH",
  /** THREE_DOMAIN_SECURE_CHALLENGE - Takes the customer through the steps to resolve a Three Domain Secure Challenge. */
  THREE_DOMAIN_SECURE_CHALLENGE = "THREE_DOMAIN_SECURE_CHALLENGE",
  /**
   * LINK_ACCOUNT_POST_AUTH - DEPRECATED: This user flow still exists but is now handled as part of the
   * `GRANTLY_MANAGED_GRANT` flow (post auth requirement) - [GrantFlow](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/GrantFlow.kt).
   * See [LinkAccountPostAuthorizationHandler](https://github.com/squareup/cash-grantly/blob/main/service/src/main/kotlin/com/squareup/cash/grantly/plasma/actionhandlers/linkaccount/LinkAccountPostAuthorizationHandler.kt).
   *
   * Triggered as a subflow from Grantly's Post-Authorization Requirement Handler.
   * Shows success message to the user.
   *
   * @deprecated
   */
  LINK_ACCOUNT_POST_AUTH = "LINK_ACCOUNT_POST_AUTH",
  /** EXEMPLAR_TREEHOUSE_BLOCKER_PASSTHROUGH - Passthrough flow for sending down a treehouse path */
  EXEMPLAR_TREEHOUSE_BLOCKER_PASSTHROUGH = "EXEMPLAR_TREEHOUSE_BLOCKER_PASSTHROUGH",
  /** DISABLE_RECURRING_EQUITY_BUY - Disables a recurring equity buy */
  DISABLE_RECURRING_EQUITY_BUY = "DISABLE_RECURRING_EQUITY_BUY",
  /**
   * CREATE_OR_EDIT_RECURRING_PAYMENT - Allows a customer to create a new recurring payment between
   * themselves and another cash customer. This flow can also be re-used
   * to allow a customer to edit an existing recurring payment between
   * themselves and  another customer.
   */
  CREATE_OR_EDIT_RECURRING_PAYMENT = "CREATE_OR_EDIT_RECURRING_PAYMENT",
  /** REFUND_GIFT_CARD_PAYMENT - Flow to refund a gift card payment */
  REFUND_GIFT_CARD_PAYMENT = "REFUND_GIFT_CARD_PAYMENT",
  /** LINK_CARD - Flow to link a card */
  LINK_CARD = "LINK_CARD",
  /** SPICE_REQUEST - Flow to request spice from spacing guild (Notary) */
  SPICE_REQUEST = "SPICE_REQUEST",
  /** HAS_SUFFICIENT_FUNDS_OR_LINKED_CARD - Flow to link a card when a customer doesn't have sufficient funds or a linked card for a purchase. */
  HAS_SUFFICIENT_FUNDS_OR_LINKED_CARD = "HAS_SUFFICIENT_FUNDS_OR_LINKED_CARD",
  /** INITIATE_IN_APP_BROWSER_FOR_OFFER - Enter in-app shopping flow with associated for Offers (i.e. Boosts, CAP Incentive) * */
  INITIATE_IN_APP_BROWSER_FOR_OFFER = "INITIATE_IN_APP_BROWSER_FOR_OFFER",
  /** EXEMPLAR_ORDER_PIZZA_MULTI_VARIANT - A fake flow for Exemplar code */
  EXEMPLAR_ORDER_PIZZA_MULTI_VARIANT = "EXEMPLAR_ORDER_PIZZA_MULTI_VARIANT",
  /** PLASMA_SUBFLOW_WRAPPER - A generic flow for wrapping known Flow as a SubFlow, meant for Client-Driven Flow purposes */
  PLASMA_SUBFLOW_WRAPPER = "PLASMA_SUBFLOW_WRAPPER",
  /** ORDER_RABBITS - Flow to order rabbits via a fictional product, Rabbits on Demand. For demo purposes only. */
  ORDER_RABBITS = "ORDER_RABBITS",
  /** BORROW_UNDERWRITING - Determine if the customer is eligible for a Borrow credit line. */
  BORROW_UNDERWRITING = "BORROW_UNDERWRITING",
  /** PLASMA_REQUIREMENT_WRAPPER - A generic flow for wrapping known Requirement as a SubFlow, meant for Client-Driven Flow purposes */
  PLASMA_REQUIREMENT_WRAPPER = "PLASMA_REQUIREMENT_WRAPPER",
  /** OVERDRAFT - Flow related to Overdraft Upsell for Banking Customers. */
  OVERDRAFT = "OVERDRAFT",
  /** ENABLE_RECURRING_EQUITY_BUY - Enables a recurring equity buy */
  ENABLE_RECURRING_EQUITY_BUY = "ENABLE_RECURRING_EQUITY_BUY",
  /** SUP_CREATE_CARD - Flow to initiate SUP card creation and checkout */
  SUP_CREATE_CARD = "SUP_CREATE_CARD",
  /** SUP_EDIT_CARD - Flow to modify an existing SUP card */
  SUP_EDIT_CARD = "SUP_EDIT_CARD",
  /** SUP_ADJUST_AMOUNT - Flow to adjust the amount on an existing SUP card */
  SUP_ADJUST_AMOUNT = "SUP_ADJUST_AMOUNT",
  /** SUP_CANCEL_CARD - Flow to cancel an existing SUP card */
  SUP_CANCEL_CARD = "SUP_CANCEL_CARD",
  /** CANCEL_P2P_FIAT_PAYMENT - Cancels a p2p fiat payment. */
  CANCEL_P2P_FIAT_PAYMENT = "CANCEL_P2P_FIAT_PAYMENT",
  /** DISABLE_RECURRING_EQUITY_BUY_V2 - Disables a recurring equity buy (Input Proto: DisableRecurringEquityBuyParams) */
  DISABLE_RECURRING_EQUITY_BUY_V2 = "DISABLE_RECURRING_EQUITY_BUY_V2",
  /** IDENTITY_HUB - Flow to initiate Identity Hub */
  IDENTITY_HUB = "IDENTITY_HUB",
  /** OVERDRAFT_ACTIVATION - Overdraft Activation Flow */
  OVERDRAFT_ACTIVATION = "OVERDRAFT_ACTIVATION",
  /** DRAKE_PRESALE_MARCH_2023 - Flow for Drake presale event in March 2023 */
  DRAKE_PRESALE_MARCH_2023 = "DRAKE_PRESALE_MARCH_2023",
  /** OVERDRAFT_PAYMENT - Overdraft Re-Payment flow. */
  OVERDRAFT_PAYMENT = "OVERDRAFT_PAYMENT",
  /** RETROACTIVE_FINANCING_INITIATE_LOAN_FLOW - Flow for initiating a loan to retroactively finance a purchase * */
  RETROACTIVE_FINANCING_INITIATE_LOAN_FLOW = "RETROACTIVE_FINANCING_INITIATE_LOAN_FLOW",
  /** REFUND_P2P_FIAT_PAYMENT - Refunds a p2p fiat payment. */
  REFUND_P2P_FIAT_PAYMENT = "REFUND_P2P_FIAT_PAYMENT",
  /** INVEST_EQUITY_CUSTOMER_ONBOARDING - Manages the customer onboarding flow for invest equity */
  INVEST_EQUITY_CUSTOMER_ONBOARDING = "INVEST_EQUITY_CUSTOMER_ONBOARDING",
  /** RETROACTIVE_FINANCING_OVERVIEW_FLOW - Flow for providing overview on the retroactive financing lending product * */
  RETROACTIVE_FINANCING_OVERVIEW_FLOW = "RETROACTIVE_FINANCING_OVERVIEW_FLOW",
  /** LINK_CARD_AUTH - Flow to re-link card with authentication (Example: 3DS authentication) * */
  LINK_CARD_AUTH = "LINK_CARD_AUTH",
  /** EDIT_PAYCHECK_ALLOCATION - Flow to edit a paycheck auto-allocation * */
  EDIT_PAYCHECK_ALLOCATION = "EDIT_PAYCHECK_ALLOCATION",
  /** DISABLE_RECURRING_CRYPTO_BUY - Disable recurring crypto buys (Input Data params: DisableRecurringCryptoBuyParams) */
  DISABLE_RECURRING_CRYPTO_BUY = "DISABLE_RECURRING_CRYPTO_BUY",
  /** DISABLE_RECURRING_CRYPTO_BUY_2 - Disable recurring crypto buys (Input: Query params) */
  DISABLE_RECURRING_CRYPTO_BUY_2 = "DISABLE_RECURRING_CRYPTO_BUY_2",
  /** UPSELL_LINKED_DEBIT - A flow to upsell a customer to link a debit card if one is not currently linked */
  UPSELL_LINKED_DEBIT = "UPSELL_LINKED_DEBIT",
  /** MULTI_APPROVE_SPONSORSHIP - A flow which handles orchestration of a set of pending sponsorships requests */
  MULTI_APPROVE_SPONSORSHIP = "MULTI_APPROVE_SPONSORSHIP",
  /** INITIATE_SEND_BITCOIN - A flow which handles sending bitcoin (e.g. via on-chain or lightning) */
  INITIATE_SEND_BITCOIN = "INITIATE_SEND_BITCOIN",
  /**
   * SKIP_SCHEDULED_RECURRING_PAYMENT - Allows a customer to skip the next scheduled
   *  recurring payment between between them and
   *  another cash customer. Customer who is skipping the scheduled
   *  recurring payment must be the sender in the recurring payment.
   */
  SKIP_SCHEDULED_RECURRING_PAYMENT = "SKIP_SCHEDULED_RECURRING_PAYMENT",
  /**
   * VIEW_PAYMENT_PLAN_FLOW - Flow for viewing the accepted loan's details, upcoming payments, and option to make
   * an early repayment.
   * Currently built for the retroactive financing product, but might be extended to
   * support other products. Also, in the future, this flow might be replaced with a
   * sync entity so that users can view the payment plan offline.
   */
  VIEW_PAYMENT_PLAN_FLOW = "VIEW_PAYMENT_PLAN_FLOW",
  /** RESEND_SPONSORSHIP_FEATURE_INVITATION_REQUEST - A flow to resend a sponsorship feature invitation request. */
  RESEND_SPONSORSHIP_FEATURE_INVITATION_REQUEST = "RESEND_SPONSORSHIP_FEATURE_INVITATION_REQUEST",
  /** APPROVE_DEPENDENT_DISPUTE - A flow for sponsors to approve a dependent's dispute */
  APPROVE_DEPENDENT_DISPUTE = "APPROVE_DEPENDENT_DISPUTE",
  /** REQUIREMENT_WRAPPER - A wrapper flow that just allows a single requirement to be used in a client route - just forwards to a requirement */
  REQUIREMENT_WRAPPER = "REQUIREMENT_WRAPPER",
  /** EDIT_BUSINESS_LOCATION_FLOW - A flow for editing a business location */
  EDIT_BUSINESS_LOCATION_FLOW = "EDIT_BUSINESS_LOCATION_FLOW",
  /** VERIFY_INSTRUMENT_ACCESS_FLOW - Flow to verify that a customer has access to their external instrument for risk purposes */
  VERIFY_INSTRUMENT_ACCESS_FLOW = "VERIFY_INSTRUMENT_ACCESS_FLOW",
  /** CREATE_SECONDARY_PROFILE - Lets a customer create a secondary profile. Prototype only. */
  CREATE_SECONDARY_PROFILE = "CREATE_SECONDARY_PROFILE",
  /** EDIT_BUSINESS_CATEGORY_FLOW - A flow for editing a business category */
  EDIT_BUSINESS_CATEGORY_FLOW = "EDIT_BUSINESS_CATEGORY_FLOW",
  /** EDIT_INSTAGRAM_FOR_C4B_FLOW - A flow for linking instagram to a c4b customer */
  EDIT_INSTAGRAM_FOR_C4B_FLOW = "EDIT_INSTAGRAM_FOR_C4B_FLOW",
  /** DISPUTE_STEP_UP - Asks customer to confirm a transaction dispute that cash has flagged as dispute abuse. */
  DISPUTE_STEP_UP = "DISPUTE_STEP_UP",
  /**
   * GLOBAL_SOURCE_OF_FUNDS_COLLECTION - Flow to collect a customer's source of wealth/funds information for
   * Global compliance work.
   */
  GLOBAL_SOURCE_OF_FUNDS_COLLECTION = "GLOBAL_SOURCE_OF_FUNDS_COLLECTION",
  /** CBCA_CAPSULE_JUNE_2023 - Flow for the Cash by Cash App merch drop, to go along with the Mood Card launch in June 2023 */
  CBCA_CAPSULE_JUNE_2023 = "CBCA_CAPSULE_JUNE_2023",
  /** INSTRUMENT_SELECTION - Flow have the customer choose 1 from a list of instruments */
  INSTRUMENT_SELECTION = "INSTRUMENT_SELECTION",
  /** CARD_ORDER_STATUS - Flow to display customer's cash card order status */
  CARD_ORDER_STATUS = "CARD_ORDER_STATUS",
  /** DISCOVER_CASH_CARD_UPSELL - Flow to initiate Cash Card order from Discovery tab */
  DISCOVER_CASH_CARD_UPSELL = "DISCOVER_CASH_CARD_UPSELL",
  /** SET_ADDRESS - Flow to add the set address requirement */
  SET_ADDRESS = "SET_ADDRESS",
  /** ACCOUNT_RECOVERY - Flow recovery your account */
  ACCOUNT_RECOVERY = "ACCOUNT_RECOVERY",
  /**
   * INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION - Flow which kicks off the crossborder-2.0 payment experience, used when the recipient is not
   * known, but their region is known
   */
  INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION = "INITIATE_MULTICURRENCY_PAYMENT_FOR_REGION",
  /**
   * INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT - A flow which kicks off the crossborder-2.0 payment experience, used when the recipient is
   * known
   */
  INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT = "INITIATE_MULTICURRENCY_PAYMENT_FOR_RECIPIENT",
  /** BORROW_ONBOARDING - Flow to help onboard new Customers to Borrow dependent on their eligibility */
  BORROW_ONBOARDING = "BORROW_ONBOARDING",
  /** INVEST_YOUR_PAYCHECK_CONFIRMATION - Flow to request confirmation of an Invest Your Paycheck allocation */
  INVEST_YOUR_PAYCHECK_CONFIRMATION = "INVEST_YOUR_PAYCHECK_CONFIRMATION",
  /** POSTBANK_RETRIEVE_BANKING_INFORMATION - Flow to retrieve banking information * */
  POSTBANK_RETRIEVE_BANKING_INFORMATION = "POSTBANK_RETRIEVE_BANKING_INFORMATION",
  /** ENABLE_BALANCE_BASED_ADD_CASH - Flow to enable balance based add cash by setting preferences * */
  ENABLE_BALANCE_BASED_ADD_CASH = "ENABLE_BALANCE_BASED_ADD_CASH",
  /** EDIT_BALANCE_BASED_ADD_CASH_MINIMUM_BALANCE_AMOUNT - Flow to edit the balance based add cash minimum amount preference * */
  EDIT_BALANCE_BASED_ADD_CASH_MINIMUM_BALANCE_AMOUNT = "EDIT_BALANCE_BASED_ADD_CASH_MINIMUM_BALANCE_AMOUNT",
  /** EDIT_BALANCE_BASED_ADD_CASH_INCREMENT_AMOUNT - Flow to edit the balance based add cash increment amount preference * */
  EDIT_BALANCE_BASED_ADD_CASH_INCREMENT_AMOUNT = "EDIT_BALANCE_BASED_ADD_CASH_INCREMENT_AMOUNT",
  /** DISABLE_BALANCE_BASED_ADD_CASH - Flow to disable balance based add cash feature * */
  DISABLE_BALANCE_BASED_ADD_CASH = "DISABLE_BALANCE_BASED_ADD_CASH",
}

export enum FlowFlowIntent {
  UNUSED = "UNUSED",
  /**
   * SPECULATIVE - The flow is created without the customer intending to complete it. This flow should not be
   * completable and the flow owner is responsible for ensuring that.
   */
  SPECULATIVE = "SPECULATIVE",
  /** INTENTIONAL - The flow is created with the customer intending to complete the goal. */
  INTENTIONAL = "INTENTIONAL",
}

/**
 * A flow can have flow-specific parameters.  Rather than introducing these parameters as
 * a custom message below, encode them in the data entry as a ByteString.
 *
 * This also contains parameters common across all flows.
 */
export interface FlowParameters {
  add_boost_flow_parameters?: FlowParametersAddBoostFlowParameters | undefined;
  recipient_payment_flow_parameters?: FlowParametersRecipientPaymentFlowParameters | undefined;
  remove_boost_flow_parameters?: FlowParametersRemoveBoostFlowParameters | undefined;
  sender_payment_flow_parameters?: FlowParametersSenderPaymentFlowParameters | undefined;
  disable_issued_card_flow_parameters?: FlowParametersDisableIssuedCardFlowParameters | undefined;
  request_physical_card_flow_parameters?: FlowParametersRequestPhysicalCardFlowParameters | undefined;
  dispute_card_transaction_flow_parameters?: FlowParametersDisputeCardTransactionFlowParameters | undefined;
  activate_physical_card_flow_parameters?: FlowParametersActivatePhysicalCardFlowParameters | undefined;
  activate_direct_deposit_account_flow_parameters?:
    | FlowParametersActivateDirectDepositAccountFlowParameters
    | undefined;
  lock_issued_card_flow_parameters?: FlowParametersLockIssuedCardFlowParameters | undefined;
  change_card_design_flow_parameters?: FlowParametersChangeCardDesignFlowParameters | undefined;
  report_card_lost_and_stolen_flow_parameters?: FlowParametersReportCardLostAndStolenFlowParameters | undefined;
  deposit_paper_cash_flow_parameters?: FlowParametersDepositPaperCashFlowParameters | undefined;
  card_expiration_flow_parameters?: FlowParametersCardExpirationFlowParameters | undefined;
  approve_sponsorship_flow_parameters?: FlowParametersApproveSponsorshipFlowParameters | undefined;
  request_sponsorship_flow_parameters?: FlowParametersRequestSponsorshipFlowParameters | undefined;
  set_or_update_password_parameters?: FlowParametersSetOrUpdatePasswordParameters | undefined;
  set_or_verify_password_parameters?: FlowParametersSetOrVerifyPasswordParameters | undefined;
  loyalty_onboarding_flow_parameters?: FlowParametersLoyaltyOnboardingFlowParams | undefined;
  accept_gift_card_flow_parameters?: FlowParametersAcceptGiftCardFlowParameters | undefined;
  convert_gift_card_to_cash_parameters?: FlowParametersConvertGiftCardToCashFlowParameters | undefined;
  enable_issued_card_flow_parameters?: FlowParametersEnableIssuedCardFlowParameters | undefined;
  loyalty_enrollment_flow_parameters?: FlowParametersLoyaltyEnrollmentFlowParams | undefined;
  refund_gift_card_flow_parameters?: FlowParametersRefundGiftCardFlowParameters | undefined;
  approve_dependent_request_parameters?:
    | FlowParametersApproveDependentRequestParameters
    | undefined;
  /** Can hold any list of key/value pairs presented within the query string. */
  query_string_parameters?:
    | FlowParametersQueryStringParameters
    | undefined;
  /** Can hold any proto encoded as a ByteString. */
  data_parameters?:
    | Uint8Array
    | undefined;
  /**
   * The call context that was set when the flow was created.
   * This is deprecated in favor of ForwardedCallContext, which provides call context
   * from recent client requests instead of at the point of flow initiation.
   *
   * @deprecated
   */
  call_context?: CallContext;
  /**
   * This field allows the caller to override the theme for all blockers in a given flow.
   *
   * If not set, Plasma will attempt to derive this field from the flow history to ensure
   * that the theme propagates to all blockers.
   */
  theme?: BlockerDescriptorTheme;
}

export enum FlowParametersAPP_OPERATING_SYSTEM {
  NOT_SET = "NOT_SET",
  ANDROID = "ANDROID",
  IOS = "IOS",
}

export interface FlowParametersAddBoostFlowParameters {
  /** Boost that is being added to the card. */
  boost_token?: string;
  /** Boost that is being replace . */
  selected_reward_token?: string;
  /** Information needed to request a new card for non-cardholders to add a boost */
  card_request_info?: CardRequestInfo;
}

export interface FlowParametersRecipientPaymentFlowParameters {
  payment_token?: string;
  client_id?: string;
  customer_token?: string;
  external_id?: string;
}

export interface FlowParametersRemoveBoostFlowParameters {
  selected_reward_token?: string;
}

export interface FlowParametersSenderPaymentFlowParameters {
  payment_token?: string;
  initiator_client_id?: string;
  initiator_customer_token?: string;
  external_id?: string;
}

export interface FlowParametersDisableIssuedCardFlowParameters {
  app_token?: string;
}

export interface FlowParametersCardExpirationFlowParameters {
  expiration_period?: string;
}

export interface FlowParametersRequestPhysicalCardFlowParameters {
  style_picker_eligible?: boolean;
  metal_card_eligible?: boolean;
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
  three_dimensional_eligible?: boolean;
  sponsorship_status?: FlowParametersRequestPhysicalCardFlowParametersSponsorshipStatus;
  browser_interaction?: BrowserInteraction;
  upsell_context?: FlowParametersRequestPhysicalCardFlowParametersUpsellContext;
  external_identifier?: string;
}

export enum FlowParametersRequestPhysicalCardFlowParametersSponsorshipStatus {
  PENDING = "PENDING",
  SPONSORED = "SPONSORED",
  SUSPENDED = "SUSPENDED",
  UNSPONSORED = "UNSPONSORED",
}

/** Upsell context shown at the confirmation screen of the RequestPhysicalCard flow. */
export interface FlowParametersRequestPhysicalCardFlowParametersUpsellContext {
  title?: string;
  /**
   * description_image_url is deprecated in favor of description_image.
   *
   * @deprecated
   */
  description_image_url?: string;
  description_image?: Image;
  description_text?: string;
  description_subtext?: string;
}

export interface FlowParametersDisputeCardTransactionFlowParameters {
  transaction_token?: string;
  authenticated_with_two_factor?: boolean;
}

export interface FlowParametersActivatePhysicalCardFlowParameters {
  is_card_delievered_within_fourteen_days?: boolean;
  is_local_image_element_supported?: boolean;
  is_form_blocker_supported?: boolean;
  is_android?: boolean;
  browser_interaction?: BrowserInteraction;
  device_id?: string;
}

export interface FlowParametersActivateDirectDepositAccountFlowParameters {
  is_local_image_element_supported?: boolean;
  browser_interaction?: BrowserInteraction;
}

/** Used by both the lock and unlock flows */
export interface FlowParametersLockIssuedCardFlowParameters {
  app_token?: string;
}

export interface FlowParametersChangeCardDesignFlowParameters {
  include_name_blocker?: boolean;
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
}

export interface FlowParametersReportCardLostAndStolenFlowParameters {
  include_name_blocker?: boolean;
  is_new_reorder_supported?: boolean;
  /** deprecated */
  is_ios?: boolean;
  /** deprecated */
  is_android?: boolean;
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
  is_three_dimensional_eligible?: boolean;
  /** Determines which mode to run this flow in. */
  flow_mode?: FlowParametersReportCardLostAndStolenFlowParametersFlowMode;
  /**
   * Used for Transaction Authentication's replace card subflow.
   * True when more blockers will be presented to the customer by the main flow after the
   * completion of this subflow. False if completion of this subflow will complete the main flow
   * as well.
   */
  subflow_more_blockers_remaining?: boolean;
}

export enum FlowParametersReportCardLostAndStolenFlowParametersFlowMode {
  /**
   * REPORT_CARD_LOST_STOLEN - The original, default flow logic. Allows customer to select whether their card was lost,
   * stolen, or damaged before going through reorder requirements.
   */
  REPORT_CARD_LOST_STOLEN = "REPORT_CARD_LOST_STOLEN",
  /**
   * REPLACE_CARD_SUBFLOW - Treats card as a Risk model suggested replacement before reordering card. Intended to be
   * used a subflow for Transaction Authentication only.
   */
  REPLACE_CARD_SUBFLOW = "REPLACE_CARD_SUBFLOW",
}

export interface FlowParametersDepositPaperCashFlowParameters {
  app_version?: string;
  app_operating_system?: FlowParametersAPP_OPERATING_SYSTEM;
}

export interface FlowParametersApproveSponsorshipFlowParameters {
  sponsored_account_token?: string;
  entry_point?: FlowParametersApproveSponsorshipFlowParametersEntryPoint;
}

export enum FlowParametersApproveSponsorshipFlowParametersEntryPoint {
  ACTIVITY = "ACTIVITY",
  NOTIFICATION = "NOTIFICATION",
  CAMPAIGN = "CAMPAIGN",
  ONBOARDING = "ONBOARDING",
}

export interface FlowParametersRequestSponsorshipFlowParameters {
  full_name?: string;
  birthdate?: Birthdate;
}

export interface FlowParametersMultiApproveSponsorshipFlowParameters {
  invitation_initiated_sponsored_account_token?: string;
  entry_point?: FlowParametersMultiApproveSponsorshipFlowParametersEntryPoint;
}

export enum FlowParametersMultiApproveSponsorshipFlowParametersEntryPoint {
  ACTIVITY = "ACTIVITY",
  NOTIFICATION = "NOTIFICATION",
  CAMPAIGN = "CAMPAIGN",
  ONBOARDING = "ONBOARDING",
}

export interface FlowParametersSetOrUpdatePasswordParameters {
  /** Allow specifying custom blocker text related to the reason for the password change. */
  create_display_config?: PasswordCreationDisplayConfig;
  verify_display_config?: PasswordVerificationDisplayConfig;
}

export interface FlowParametersSetOrVerifyPasswordParameters {
  /** Allow specifying custom blocker text related to the reason for the password verification. */
  create_display_config?: PasswordCreationDisplayConfig;
  verify_display_config?: PasswordVerificationDisplayConfig;
}

export interface FlowParametersLoyaltyOnboardingFlowParams {
  /** The token representing a cash merchant in merchantein */
  merchant_token?: string;
  phone_number_token?: string;
}

export interface FlowParametersLoyaltyEnrollmentFlowParams {
  /** The token representing a cash merchant in merchantein */
  merchant_token?: string;
}

export interface FlowParametersQueryStringParameters {
  query_parameters_list?: FlowParametersQueryStringParametersParameter[];
}

export interface FlowParametersQueryStringParametersParameter {
  key?: string;
  values?: string[];
}

export interface FlowParametersAcceptGiftCardFlowParameters {
  payment_token?: string;
}

export interface FlowParametersConvertGiftCardToCashFlowParameters {
  payment_token?: string;
  gift_card_token?: string;
}

export interface FlowParametersRefundGiftCardFlowParameters {
  payment_token?: string;
}

export interface FlowParametersEnableIssuedCardFlowParameters {
  /** allow unmasking upon unlock */
  unmask?: boolean;
}

/**
 * Specifies how to start a remittance - by providing either a region, a recipient, or a
 * transaction to repeat. If specifying the region or recipient, either a send or receive amount
 * may be specified, but not both.
 * Will be encoded into the data_parameters and used via ClientRoute.
 */
export interface FlowParametersInitRemitFlowParameters {
  target_region?: Region;
  recipient_token?: string;
  send_amount?: Money;
  receive_amount?: Money;
  repeat_transaction_token?: string;
}

/**
 * Any dependent request flow will be provided this payload. This is a shared contract from any product flow
 * that allows for sponsor approval
 */
export interface FlowParametersApproveDependentRequestParameters {
  /** Which dependent requested approval */
  dependent_customer_token?: string;
  /** Aegis' unique identifier for this request */
  product_request_token?: string;
  /**
   * Unique instance ID for the request being approved as provided by the product flow.
   * This is the same ID that was provided to the REQUEST_SPONSOR_APPROVAL requirement
   */
  external_instance_id?: string;
}
